import React, { useEffect, useMemo, useState } from 'react';

import Spinner from 'components/module/Spinner';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';
import { CeoSubmittedFormInstance } from 'services/ceoSubmittedForm';
import { EnrollmentFormServiceInstance } from 'services/enrollmentFormService';
import { SubmittedFormInstance } from 'services/submittedFormService';
import { SurveyServiceInstance } from 'services/surveyService';
import { NotFoundError } from 'types/Errors';
import { Key } from 'types/Key';
import { currencyToFloat } from 'utils/currencyToFloat';
import { getMaxMinAvg } from 'utils/getMaxMinAvg';

import { DiagnosticGeneratedProps, RoiData, Summary } from './Diagnostic.props';
import DiagnosticView from './Diagnostic.view';
import {QUESTION_TYPE} from 'types/Enum';


const Diagnostic = (): JSX.Element => {
  // MARK:- Hooks
  const { enrollmentId } = useParams() as { enrollmentId: string };

  // MARK:- State
  const [errors, setErrors] = useState<string[]>([]);

  // MARK:- Queries
  const getEnrollmentForm = useQuery(
    ['enrollmentForm', enrollmentId],
    () => EnrollmentFormServiceInstance.getById(enrollmentId),
    {
      enabled: !!enrollmentId,
    }
  );

  const surveyId = getEnrollmentForm.data?.body?.surveyId as Key;

  const getSurveyAnswers = useQuery(
    'surveyAnswers',
    () => SubmittedFormInstance.getListByEnrollmentId(enrollmentId),
    {
      enabled: !!enrollmentId,
    }
  );

  const getCeoSubmittedForm = useQuery(
    'ceoSubmittedForm',
    () => CeoSubmittedFormInstance.getByEnrollmentId(enrollmentId),
    {
      enabled: !!enrollmentId,
    }
  );

  const getSurvey = useQuery(
    ['survey', surveyId?.id || surveyId?.name],
    () => SurveyServiceInstance.getById(surveyId?.id || surveyId?.name || ''),
    {
      enabled: !!surveyId?.id || !!surveyId?.name,
    }
  );

  // MARK:- Variables
  const surveySections = getSurvey.data?.body.sections;
  const surveyOrder = getSurvey.data?.body.order;
  const surveyAnswers = getSurveyAnswers.data?.body;
  const ceoAnswer = getCeoSubmittedForm.data?.body;
  const employeeEmails = useMemo(
    () => getEnrollmentForm.data?.body.employeeEmails || [],
    [getEnrollmentForm.data?.body.employeeEmails]
  );

  const companyAnnualRevenue = currencyToFloat(
    ceoAnswer?.questions[1]?.value || ''
  );

  const currentNetIncomePct = ceoAnswer?.questions[3]?.value;
  const desiredNetIncomePct = ceoAnswer?.questions[4]?.value;

  // MARK:- Memos
  const companyNetIncome = useMemo(() => {
    if (!companyAnnualRevenue || !currentNetIncomePct) return 0;

    return companyAnnualRevenue * (currencyToFloat(currentNetIncomePct) / 100);
  }, [companyAnnualRevenue, currentNetIncomePct]);

  const {
    summary,
    impact,
  }: { summary: Summary; impact: RoiData } = useMemo(() => {
    let summary: Summary = {};
    let imp: RoiData = {};

    if (!surveyOrder || !surveySections) {
      return { impact: imp, summary };
    }

    for (let x = 0; x < surveyOrder.length; x++) {
      const key: string = surveyOrder[x];
      let allFavorable = 0;
      let allResponses = 0;

      summary = {
        ...summary,
        [key]: {
          diagnostics: [],
          categoryScore: 0,
          financialImpact: 0,
        },
      };

      imp = {
        ...imp,
        [key]: {
          avg: 0,
          low: 0,
          high: 0,
        },
      };

      surveySections[key].questions.forEach((currentQuestion, ndx) => {
        const scoreArr: number[] = [];
        const impactArr: number[] = [];

        const summaryOfQuestion = {
          question: currentQuestion.question,
          totalResponses: 0,
          favorable: 0,
          type: currentQuestion.type,
          inputResponses: [] as Array<string>,
          numericScore: 0,
          improvementScore: 0,
          avgNumericScore: 0,
        };

        surveyAnswers?.forEach((surveyAnswer, qNum) => {
          if (key in surveyAnswer.answers === false) {
            return;
          }

          const { value, type, question } = surveyAnswer.answers[key].questions[
            ndx
          ];

          summaryOfQuestion.question = `${ndx + 1}. ${question}`;
          summaryOfQuestion.totalResponses += 1;

          if (value.trim() === '') {
            return;
          }

          if (type == QUESTION_TYPE.Points) {
            allResponses += 1;

            if (+value >= 4) {
              summaryOfQuestion.favorable += 1;
              allFavorable += 1;
            }
          }

          if (type == QUESTION_TYPE.Booblean) {
            allResponses += 1;
            if (value === 'true') {
              summaryOfQuestion.favorable += 1;
              allFavorable += 1;
            }
          }

          if (type == QUESTION_TYPE.Input) {
            summaryOfQuestion.inputResponses.push(value);
          }

          if (type == QUESTION_TYPE.Numeric) {
            scoreArr.push(+value);
            summaryOfQuestion.numericScore += +value;
          }

          // Last question of each section resolve to an impact percentage
          if (ndx === surveySections[key].questions.length - 1) {
            impactArr.push(+value);
          }
        });

        const categoryScore = allFavorable / allResponses;
        const avgNumericScore =
          summaryOfQuestion.numericScore / scoreArr.length;

        summaryOfQuestion.avgNumericScore = avgNumericScore;

        summary[key] = {
          ...summary[key],
          diagnostics: [...summary[key].diagnostics, summaryOfQuestion],
          categoryScore: categoryScore * 100,
          financialImpact: (avgNumericScore / 100) * companyAnnualRevenue,
        };

        imp[key] = getMaxMinAvg(scoreArr);
      });
    }

    return { impact: imp, summary };
  }, [surveySections, surveyOrder, surveyAnswers, companyAnnualRevenue]);

  const unsubmittedEmails = useMemo(() => {
    const submittedEmails: string[] =
      surveyAnswers?.map((answer) => answer.submittedBy) || [];

    const filtered = employeeEmails.filter(
      (email: string) => !submittedEmails.includes(email) && email !== ''
    );

    return filtered;
  }, [employeeEmails, surveyAnswers]);

  // MARK:- Effects
  useEffect(() => {
    const errArr: string[] = [];

    if (getCeoSubmittedForm.error instanceof NotFoundError) {
      errArr.push('CEO has not answered the survey yet');
    }

    if (
      getSurveyAnswers?.isSuccess &&
      getSurveyAnswers?.data?.body.length === 0
    ) {
      errArr.push('No one has answered the survey yet!');
    }

    setErrors(errArr);
  }, [
    getCeoSubmittedForm.error,
    getSurveyAnswers?.isSuccess,
    getSurveyAnswers?.data?.body.length,
  ]);

  // MARK:- Render
  const isLoading =
    getEnrollmentForm.isLoading ||
    getSurveyAnswers.isLoading ||
    getCeoSubmittedForm.isLoading ||
    getSurvey.isLoading;

  const generatedProps: DiagnosticGeneratedProps = {
    enrollmentForm: getEnrollmentForm.data?.body,
    survey: getSurvey.data?.body,
    ceoAnswer,
    impact,
    unsubmittedEmails,
    summary,
    errors,
    companyAnnualRevenue,
    isLoading,
    currentNetIncome: companyNetIncome,
    currentNetIncomePct: currencyToFloat(currentNetIncomePct || ''),
    desiredNetIncomePct: currencyToFloat(desiredNetIncomePct || ''),
    unfinishedEmail: {
      email: '',
      ceoName: getEnrollmentForm.data?.body.ceoName || '',
      companyName: getEnrollmentForm.data?.body.companyName || '',
      enrollmentFormId: getEnrollmentForm.data?.body.id || '',
      surveyId: getSurvey.data?.body.id || '',
    },
    companyName: getEnrollmentForm.data?.body.companyName || '',
  };

  if (isLoading) {
    return <Spinner />;
  }

  return <DiagnosticView {...generatedProps} />;
};

export default Diagnostic;
